import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { Albaran, EstadoAlbaran, PaginaAlbaranes } from '../api/modelos'
import { abrirPdf } from '../componentes/descargas'
import { TAMANO_PAGINA } from '../componentes/paginacion'

export interface FiltrosAlbaranes {
  desde: string
  hasta: string
  cliente: string
  estado: EstadoAlbaran | ''
  page: number
}

/** El backend llama `limit` al tamaño de página; 15, como todos los listados. */
export const TAMANO_ALBARANES = TAMANO_PAGINA

/** Albaranes internos por fecha y cliente (RF-43). */
export function useAlbaranes(f: FiltrosAlbaranes) {
  const api = useApi()
  return useQuery<PaginaAlbaranes, ErrorApi>({
    queryKey: ['albaranes', f],
    queryFn: ({ signal }) =>
      api.pedir<PaginaAlbaranes>('/api/albaranes', {
        parametros: { desde: f.desde, hasta: f.hasta, cliente: f.cliente, estado: f.estado, page: f.page, limit: TAMANO_ALBARANES },
        senal: signal,
      }),
    placeholderData: keepPreviousData,
  })
}

export function useAlbaran(id: string) {
  const api = useApi()
  return useQuery<Albaran, ErrorApi>({
    queryKey: ['albaran', id],
    queryFn: ({ signal }) => api.pedir<Albaran>(`/api/albaranes/${id}`, { senal: signal }),
  })
}

export function useAnularAlbaran(id: string) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<Albaran, ErrorApi, { motivo: string }>({
    mutationFn: ({ motivo }) => api.pedir<Albaran>(`/api/albaranes/${id}/anular`, { metodo: 'POST', cuerpo: { motivo } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['albaranes'] })
      void qc.invalidateQueries({ queryKey: ['albaran', id] })
    },
  })
}

/** Descarga el PDF del albarán y lo abre. */
export function usePdfAlbaran() {
  const api = useApi()
  return useMutation<void, ErrorApi, { id: string; numero: string }>({
    mutationFn: async ({ id, numero }) => abrirPdf(await api.pedirArchivo(`/api/albaranes/${id}/pdf`), `${numero}.pdf`),
  })
}
